/**
 * 역할·권한
 * ---------------------------------------------------------------------------
 * 누가 무엇을 할 수 있는지 정한다. 지금까지는 API 만 있어 권한 조정을 DB 로
 * 해야 했다.
 *
 * 화면에서 지키는 것
 *
 *   시스템 역할은 고치지 않는다
 *     기업 소유자·기업 관리자·일반 사용자는 플랫폼이 정의한다. 권한을 보여
 *     주되 잠근다 — 다르게 쓰고 싶으면 복사해서 새 역할을 만든다. 서버도
 *     막지만, 눌러본 뒤 거절당하는 것보다 왜 못 고치는지 먼저 보이는 편이 낫다.
 *
 *   미구독 모듈의 권한은 회색으로 둔다
 *     구독하지 않은 제품의 권한을 역할에 넣을 수는 있다(나중에 구독하면 바로
 *     쓰인다). 다만 지금은 효력이 없다는 사실이 보여야 한다 — 권한을 줬는데
 *     화면이 안 열린다는 문의가 여기서 나온다.
 *
 *   민감 권한은 표시한다
 *     개인정보 내보내기·감사로그 조회처럼 감사 대상인 권한이다. 체크박스가
 *     스무 개씩 늘어선 화면에서 그냥 다 켜지는 것을 막는다.
 *
 *   쓰는 사람이 있으면 지우지 못한다
 *     역할을 지우면 그 사람들의 권한이 사라진다. 먼저 옮기게 한다.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { CopyOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { hasPermission, useAuth } from '../../lib/auth-store';
import RoleMembersPanel from './RoleMembersPanel';

const { Text, Paragraph } = Typography;

interface Role {
  roleId: string;
  roleCode: string;
  roleName: string;
  description: string | null;
  roleScope: string;
  isSystem: boolean;
  isDefault: boolean;
  userCount: number;
  permissions: string[];
}

interface PermissionGroup {
  moduleCode: string;
  moduleName: string;
  productCode: string;
  permissions: Array<{
    key: string;
    name: string;
    isSensitive: boolean;
    entitled: boolean;
  }>;
}

export default function RolePage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const { profile } = useAuth();
  const [form] = Form.useForm();

  const canManage = hasPermission(profile, 'platform.role:manage');

  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<'create' | Role | null>(null);
  /** 복사해서 만들 때의 원본. 시스템 역할을 본떠 만들 때 쓴다. */
  const [copyFrom, setCopyFrom] = useState<Role | null>(null);
  const [checked, setChecked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * 「권한」 탭에서 직접 고치는 중인 값.
   *
   * ## 왜 팝업 말고 탭에서 고치나
   * 권한은 스무 칸이 넘는다. 고치려면 [수정] 을 눌러 팝업을 열고, 이름·코드
   * 칸을 지나 아래로 스크롤해야 권한이 나온다. 정작 바꾸고 싶은 것은 권한
   * 하나인데 이름 칸부터 다시 보게 되는 셈이다.
   *
   * 보고 있는 그 자리에서 켜고 끄게 한다. null 이면 읽기 전용이다.
   */
  const [permDraft, setPermDraft] = useState<string[] | null>(null);
  const [permSaving, setPermSaving] = useState(false);
  /** 시스템 역할을 고칠 때 만들어질 새 역할의 이름 */
  const [newRoleName, setNewRoleName] = useState('');

  const roles = useQuery<Role[], NormalizedError>({
    queryKey: ['roles'],
    queryFn: async () => (await apiClient.get<Role[]>('/roles')).data,
  });

  const catalog = useQuery<PermissionGroup[], NormalizedError>({
    queryKey: ['roles', 'permissions'],
    // 권한 카탈로그는 배포로만 바뀐다. 화면을 옮길 때마다 받지 않는다.
    staleTime: 10 * 60 * 1000,
    queryFn: async () => (await apiClient.get<PermissionGroup[]>('/roles/permissions')).data,
  });

  // 처음 열면 첫 역할을 고른다. 빈 오른쪽 칸을 보여 줄 이유가 없다.
  useEffect(() => {
    if (!selected && roles.data?.length) setSelected(roles.data[0]!.roleId);
  }, [roles.data, selected]);

  const current = roles.data?.find((r) => r.roleId === selected) ?? null;

  /** 모듈별로 묶은 카탈로그. 권한 키 → 정의도 함께 만든다. */
  const byKey = useMemo(() => {
    const map = new Map<string, PermissionGroup['permissions'][number]>();
    for (const group of catalog.data ?? []) {
      for (const permission of group.permissions) map.set(permission.key, permission);
    }
    return map;
  }, [catalog.data]);

  /* ---- 편집 ---- */

  useEffect(() => {
    if (!editing) return;
    setFormError(null);

    if (editing === 'create') {
      const base = copyFrom;
      form.setFieldsValue({
        roleCode: base ? `${base.roleCode}_COPY` : '',
        roleName: base ? `${base.roleName} 복사본` : '',
        description: base?.description ?? '',
        isDefault: false,
      });
      setChecked(base ? [...base.permissions] : []);
    } else {
      form.setFieldsValue({ ...editing });
      setChecked([...editing.permissions]);
    }
  }, [editing, copyFrom, form]);

  const save = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    const payload = {
      roleCode: values.roleCode,
      roleName: values.roleName,
      description: values.description || undefined,
      roleScope: 'TENANT' as const,
      isDefault: Boolean(values.isDefault),
      permissions: checked,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing === 'create') {
        const { data } = await apiClient.post<{ roleId: string }>('/roles', payload);
        message.success('역할을 만들었습니다.');
        setSelected(data.roleId);
      } else if (editing) {
        await apiClient.put(`/roles/${editing.roleId}`, payload);
        message.success('역할을 수정했습니다.');
      }
      setEditing(null);
      setCopyFrom(null);
      await client.invalidateQueries({ queryKey: ['roles'] });
    } catch (err) {
      setFormError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (role: Role) => {
    try {
      await apiClient.delete(`/roles/${role.roleId}`);
      message.success('역할을 지웠습니다.');
      if (selected === role.roleId) setSelected(null);
      await client.invalidateQueries({ queryKey: ['roles'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  /* ---- 권한 선택 ---- */

  /*
   * 체크박스가 어느 상태를 건드릴지.
   *
   * 팝업(역할 추가·수정)과 권한 탭이 같은 나무를 쓴다. 둘이 따로 상태를
   * 들면 나무도 둘이 되고, 고칠 때마다 한쪽만 고치게 된다.
   */
  const setValues = (
    target: 'modal' | 'tab',
    next: (prev: string[]) => string[],
  ) => {
    if (target === 'modal') setChecked(next);
    else setPermDraft((prev) => next(prev ?? []));
  };

  const toggleGroup = (
    group: PermissionGroup,
    on: boolean,
    target: 'modal' | 'tab' = 'modal',
  ) => {
    const keys = group.permissions.map((p) => p.key);
    setValues(target, (prev) =>
      on ? [...new Set([...prev, ...keys])] : prev.filter((k) => !keys.includes(k)),
    );
  };

  /** 역할을 바꿔 고르면 쓰던 초안은 버린다. 남의 역할에 붙으면 큰일이다. */
  useEffect(() => {
    setPermDraft(null);
    setNewRoleName(current ? `${current.roleName} (우리 회사)` : '');
    // current.roleName 까지 의존성에 넣으면 이름을 고치는 중에 되돌아간다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /**
   * 권한 탭에서 고친 것을 저장한다.
   *
   * 우리 회사 역할이면 **그 역할을 고치고**, 시스템 역할이면 원본은 그대로
   * 두고 **복사본을 만든다.** 시스템 역할은 전 고객사가 함께 쓰는 정의라
   * 고칠 수 없는데, 그렇다고 화면을 막아 두면 역할이 전부 시스템 역할인
   * 기업에서는 권한을 조정할 길이 아예 없어 보인다.
   */
  const savePermissions = async () => {
    if (!current || !permDraft) return;

    /* 코드는 사람이 보지 않는다. 원본 코드에서 기계적으로 만든다. */
    const makeCode = () =>
      `${current.roleCode}_${Date.now().toString(36).toUpperCase()}`.slice(0, 50);

    setPermSaving(true);
    try {
      if (current.isSystem) {
        const name = newRoleName.trim();
        if (!name) {
          message.error('새 역할의 이름을 적어 주세요.');
          return;
        }
        const { data } = await apiClient.post<{ roleId: string }>('/roles', {
          roleCode: makeCode(),
          roleName: name,
          description: `${current.roleName} 을(를) 본떠 만든 우리 회사 역할`,
          roleScope: 'TENANT',
          isDefault: false,
          permissions: permDraft,
        });
        message.success(
          `「${name}」 역할을 만들었습니다. 「구성원」 탭에서 사람을 더해 주세요.`,
        );
        setPermDraft(null);
        await client.invalidateQueries({ queryKey: ['roles'] });
        // 만든 역할로 옮겨 준다 — 바로 구성원을 넣는 것이 다음 할 일이다.
        setSelected(data.roleId);
        return;
      }

      await apiClient.put(`/roles/${current.roleId}`, {
        roleCode: current.roleCode,
        roleName: current.roleName,
        description: current.description || undefined,
        roleScope: current.roleScope,
        isDefault: current.isDefault,
        permissions: permDraft,
      });
      message.success(
        `${current.roleName} 의 권한을 저장했습니다. ` +
          '이 역할을 가진 분들은 다음 요청부터 바뀐 권한으로 움직입니다.',
      );
      setPermDraft(null);
      await client.invalidateQueries({ queryKey: ['roles'] });
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setPermSaving(false);
    }
  };

  const permissionTree = (
    editable: boolean,
    values: string[],
    target: 'modal' | 'tab' = 'modal',
  ) => (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {(catalog.data ?? []).map((group) => {
        const keys = group.permissions.map((p) => p.key);
        const on = keys.filter((k) => values.includes(k));
        const entitled = group.permissions.some((p) => p.entitled);

        return (
          <div key={group.moduleCode}>
            <Space size={6} style={{ marginBottom: 6 }}>
              {editable ? (
                <Checkbox
                  checked={on.length === keys.length && keys.length > 0}
                  indeterminate={on.length > 0 && on.length < keys.length}
                  onChange={(e) => toggleGroup(group, e.target.checked, target)}
                >
                  <Text strong>{group.moduleName}</Text>
                </Checkbox>
              ) : (
                <Text strong>{group.moduleName}</Text>
              )}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {group.moduleCode}
              </Text>
              {!entitled && (
                <Tooltip title="구독하지 않은 모듈입니다. 권한을 줘도 지금은 효력이 없습니다.">
                  <Tag>미구독</Tag>
                </Tooltip>
              )}
            </Space>

            <Row gutter={[8, 4]} style={{ paddingLeft: editable ? 24 : 0 }}>
              {group.permissions.map((permission) => (
                <Col xs={24} md={12} xl={8} key={permission.key}>
                  {editable ? (
                    <Checkbox
                      checked={values.includes(permission.key)}
                      onChange={(e) =>
                        setValues(target, (prev) =>
                          e.target.checked
                            ? [...new Set([...prev, permission.key])]
                            : prev.filter((k) => k !== permission.key),
                        )
                      }
                    >
                      <PermissionLabel permission={permission} />
                    </Checkbox>
                  ) : values.includes(permission.key) ? (
                    <PermissionLabel permission={permission} />
                  ) : null}
                </Col>
              ))}
            </Row>
          </div>
        );
      })}
    </Space>
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        size="small"
        title="역할·권한"
        extra={
          <Space>
            {canManage && (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setCopyFrom(null);
                  setEditing('create');
                }}
              >
                역할 추가
              </Button>
            )}
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => void client.invalidateQueries({ queryKey: ['roles'] })}
            />
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          역할에 권한을 묶고, 구성원에게 역할을 줍니다. 역할을 고르면 오른쪽에서 그 역할을
          가진 사람을 보고 여러 명을 한 번에 넣고 뺄 수 있습니다. <b>한 사람이 여러 역할을
          가져도 되고</b>, 그때 할 수 있는 일은 가진 역할을 모두 합친 것이 됩니다. 시스템
          역할은 고칠 수 없으니, 다르게 쓰려면 복사해서 새로 만드세요.
        </Paragraph>
      </Card>

      {roles.isError && <Alert type="error" showIcon message={errorText(roles.error)} />}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <Card
          size="small"
          style={{ width: 300, flex: '0 0 300px' }}
          styles={{ body: { padding: 0 } }}
        >
          <Table<Role>
            rowKey="roleId"
            size="small"
            showHeader={false}
            loading={roles.isLoading}
            pagination={false}
            dataSource={roles.data ?? []}
            rowClassName={(row) => (row.roleId === selected ? 'ant-table-row-selected' : '')}
            onRow={(row) => ({
              onClick: () => setSelected(row.roleId),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: '역할',
                render: (_, row) => (
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <Space size={6}>
                      <Text strong={row.roleId === selected}>{row.roleName}</Text>
                      {row.isSystem && <Tag>시스템</Tag>}
                      {row.isDefault && <Tag color="blue">기본</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.roleCode} · 권한 {row.permissions.length}개 ·{' '}
                      <Badge
                        count={row.userCount}
                        showZero
                        color={row.userCount > 0 ? '#1677ff' : '#d9d9d9'}
                        style={{ fontSize: 10 }}
                      />{' '}
                      명
                    </Text>
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card
          size="small"
          style={{ flex: 1, minWidth: 0 }}
          title={current ? `${current.roleName} (${current.roleCode})` : '역할'}
          extra={
            current &&
            canManage && (
              <Space>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    setCopyFrom(current);
                    setEditing('create');
                  }}
                >
                  복사해서 만들기
                </Button>
                {!current.isSystem && (
                  <>
                    <Button size="small" onClick={() => setEditing(current)}>
                      수정
                    </Button>
                    <Popconfirm
                      title="이 역할을 지울까요?"
                      description={
                        current.userCount > 0
                          ? `${current.userCount}명이 쓰고 있어 지울 수 없습니다. 먼저 다른 역할로 옮기세요.`
                          : '되돌릴 수 없습니다.'
                      }
                      okText="삭제"
                      cancelText="취소"
                      okButtonProps={{ disabled: current.userCount > 0 }}
                      onConfirm={() => void remove(current)}
                    >
                      <Button size="small" danger>
                        삭제
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            )
          }
        >
          {!current ? (
            <Empty description="역할을 고르세요" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {current.description && (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {current.description}
                </Paragraph>
              )}

              <Tabs
                size="small"
                items={[
                  {
                    key: 'members',
                    label: `구성원 (${current.userCount})`,
                    children: (
                      <RoleMembersPanel
                        // 역할을 바꿔 고르면 안의 상태(선택 등)를 새로 시작한다.
                        key={current.roleId}
                        roleId={current.roleId}
                        roleName={current.roleName}
                        canManage={canManage}
                      />
                    ),
                  },
                  {
                    key: 'permissions',
                    label: `권한 (${
                      permDraft ? permDraft.length : current.permissions.length
                    })`,
                    children: (
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {/*
                          ## 시스템 역할은 왜 못 고치는가
                          「기업 소유자」·「일반 사용자」 같은 역할은 tenant_id 가
                          비어 있는 **전 기업 공용** 행이다. 여기서 한 칸을 끄면
                          우리 회사가 아니라 모든 고객사에서 그 권한이 사라진다.
                          그래서 막아 둔다 — 서버도 막지만 왜 못 고치는지 먼저
                          보이는 편이 낫다.

                          대신 빠져나갈 길을 **이 자리에** 둔다. 예전에는 위쪽
                          머리글의 [복사해서 만들기] 를 찾아 올라가야 했는데,
                          권한을 보다가 "이 중 하나만 빼고 싶다" 고 생각한 사람은
                          바로 여기서 막힌다.
                        */}
                        {/*
                          ## 시스템 역할도 **여기서 바로 켜고 끈다**

                          전에는 「고칠 수 없습니다」만 띄우고 [복사해서 만들기]
                          단추를 줬다. 그런데 대부분의 기업은 역할이 전부 시스템
                          역할이라(우리 회사 역할이 하나도 없다) 화면 어디에도
                          체크박스가 없었고, "권한을 수정하는 기능이 없다" 로
                          읽혔다. 실제로 그렇게 보였다.

                          그래서 체크박스는 열어 두고, **저장하는 순간** 원본
                          대신 우리 회사 전용 복사본을 만든다. 전 고객사가 함께
                          쓰는 정의는 그대로 지켜지면서, 쓰는 사람은 "여기서
                          켜고 저장" 한 번으로 끝난다.
                        */}
                        {current.isSystem && canManage && !permDraft && (
                          <Alert
                            type="info"
                            showIcon
                            message="바꾸시면 우리 회사 전용 역할로 저장됩니다"
                            description={
                              <span style={{ fontSize: 12 }}>
                                이 역할은 <b>모든 고객사가 함께 쓰는 정의</b>라 원본은
                                바뀌지 않습니다. 아래에서 필요한 권한을 켜고 끄신 뒤
                                저장하시면, 그 내용으로 <b>우리 회사 전용 역할</b>이
                                만들어집니다. 그 다음 「구성원」 탭에서 사람을 옮기시면
                                됩니다.
                              </span>
                            }
                            action={
                              <Button
                                size="small"
                                type="primary"
                                icon={<CopyOutlined />}
                                onClick={() => setPermDraft([...current.permissions])}
                              >
                                권한 고치기
                              </Button>
                            }
                          />
                        )}

                        {current.isSystem && !canManage && (
                          <Alert
                            type="info"
                            showIcon
                            message="플랫폼이 정의한 역할입니다"
                            description="권한을 바꾸려면 역할 관리 권한이 필요합니다."
                          />
                        )}

                        {/* 고치는 중일 때만 뜨는 저장 줄 */}
                        {permDraft && (
                          <Alert
                            type="warning"
                            showIcon
                            message={
                              current.isSystem
                                ? '저장하면 우리 회사 전용 역할이 새로 만들어집니다'
                                : '저장하지 않은 변경이 있습니다'
                            }
                            description={
                              current.isSystem ? (
                                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                  <span style={{ fontSize: 12 }}>
                                    「{current.roleName}」은(는) 그대로 두고, 지금 켜 두신
                                    권한 <b>{permDraft.length}개</b>로 새 역할을 만듭니다.
                                    기존 구성원은 옮기지 않으니, 만든 뒤 「구성원」 탭에서
                                    필요한 분만 더해 주세요.
                                  </span>
                                  <Input
                                    size="small"
                                    style={{ maxWidth: 320 }}
                                    value={newRoleName}
                                    onChange={(e) => setNewRoleName(e.target.value)}
                                    placeholder="새 역할 이름"
                                    addonBefore="이름"
                                  />
                                </Space>
                              ) : (
                                <span style={{ fontSize: 12 }}>
                                  이 역할을 가진 <b>{current.userCount}명</b>의 권한이 함께
                                  바뀝니다. 저장하면 다음 요청부터 적용됩니다.
                                </span>
                              )
                            }
                            action={
                              <Space>
                                <Button size="small" onClick={() => setPermDraft(null)}>
                                  되돌리기
                                </Button>
                                <Button
                                  size="small"
                                  type="primary"
                                  loading={permSaving}
                                  onClick={savePermissions}
                                >
                                  {current.isSystem ? '이 내용으로 역할 만들기' : '저장'}
                                </Button>
                              </Space>
                            }
                          />
                        )}

                        {/*
                          우리 회사 역할이면 **보던 자리에서 바로** 켜고 끈다.
                          팝업을 열어 이름 칸부터 다시 보게 할 이유가 없다.
                        */}
                        {!current.isSystem && canManage && !permDraft && (
                          <Space>
                            <Button
                              size="small"
                              onClick={() => setPermDraft([...current.permissions])}
                            >
                              권한 고치기
                            </Button>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              켜고 끈 뒤 [저장] 을 누르시면 됩니다.
                            </Text>
                          </Space>
                        )}

                        {permDraft ? (
                          permissionTree(true, permDraft, 'tab')
                        ) : current.permissions.length === 0 ? (
                          <Empty
                            description="이 역할에 부여된 권한이 없습니다"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        ) : (
                          permissionTree(false, current.permissions)
                        )}
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          )}
        </Card>
      </div>

      <Modal
        open={Boolean(editing)}
        title={
          editing === 'create'
            ? copyFrom
              ? `${copyFrom.roleName} 복사해서 만들기`
              : '역할 추가'
            : '역할 수정'
        }
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        width={860}
        onCancel={() => {
          setEditing(null);
          setCopyFrom(null);
        }}
        onOk={() => void save()}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {formError && <Alert type="error" showIcon message={formError} />}

          <Form form={form} layout="vertical" requiredMark={false}>
            <Space size="middle" style={{ display: 'flex' }} align="start">
              <Form.Item
                name="roleCode"
                label="역할 코드"
                style={{ flex: 1 }}
                rules={[
                  { required: true, message: '코드를 입력하세요' },
                  {
                    pattern: /^[A-Z][A-Z0-9_]{1,49}$/,
                    message: '영문 대문자로 시작하는 2~50자 (대문자·숫자·밑줄)',
                  },
                ]}
                extra={editing === 'create' ? undefined : '등록 후 바꿀 수 없습니다'}
              >
                <Input placeholder="SITE_MANAGER" disabled={editing !== 'create'} />
              </Form.Item>
              <Form.Item
                name="roleName"
                label="역할 이름"
                style={{ flex: 1 }}
                rules={[{ required: true, message: '이름을 입력하세요' }]}
              >
                <Input placeholder="사업장 담당자" />
              </Form.Item>
            </Space>

            <Form.Item name="description" label="설명">
              <Input.TextArea rows={2} placeholder="이 역할이 무엇을 하는 사람인지 적어 둡니다" />
            </Form.Item>

            <Form.Item
              name="isDefault"
              label="새 구성원의 기본 역할"
              valuePropName="checked"
              extra="구성원을 등록할 때 역할을 고르지 않으면 이 역할이 들어갑니다"
            >
              <Switch />
            </Form.Item>
          </Form>

          <Card size="small" title={`권한 (${checked.length}개 선택)`}>
            <div style={{ maxHeight: 340, overflowY: 'auto' }}>
              {catalog.isLoading ? (
                <Text type="secondary">권한 목록을 불러오는 중…</Text>
              ) : (
                permissionTree(true, checked)
              )}
            </div>
          </Card>

          {checked.some((k) => byKey.get(k)?.isSensitive) && (
            <Alert
              type="warning"
              showIcon
              message="민감 권한이 포함되어 있습니다"
              description={
                <>
                  개인정보 내보내기·감사로그 조회처럼 감사 대상인 권한입니다. 이 역할을 받은
                  사람의 행위는 모두 기록에 남습니다:{' '}
                  {checked
                    .filter((k) => byKey.get(k)?.isSensitive)
                    .map((k) => byKey.get(k)?.name ?? k)
                    .join(', ')}
                </>
              }
            />
          )}
        </Space>
      </Modal>
    </Space>
  );
}

function PermissionLabel({
  permission,
}: {
  permission: PermissionGroup['permissions'][number];
}) {
  return (
    <Space size={4}>
      <Text style={{ opacity: permission.entitled ? 1 : 0.45 }}>{permission.name}</Text>
      {permission.isSensitive && (
        <Tooltip title="민감 권한 — 사용 내역이 감사로그에 남습니다">
          <WarningOutlined style={{ color: '#d46b08' }} />
        </Tooltip>
      )}
    </Space>
  );
}
